import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type {
  ActiveUsersResponse,
  AppListResponse,
  AppResponse,
  AppSummary,
  ActivateReleaseInput,
  AnalyticsParams,
  AuditListResponse,
  AuditQuery,
  BuildListQuery,
  BuildListResponse,
  BuildResponse,
  BuildSummary,
  ChangePasswordRequest,
  CopyReleaseInput,
  CreateAppRequest,
  CreateChannelRequest,
  CreateUserRequest,
  DeleteBuildResponse,
  DeleteReleaseResponse,
  FeatureDetailParams,
  FeatureDetailResponse,
  FeatureUsageQuery,
  FeatureUsageResponse,
  FeatureUsageRow,
  HaltReleaseInput,
  KnownTargetsResponse,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  OverviewParams,
  OverviewResponse,
  PromoteBuildInput,
  PromoteBuildResponse,
  PublishReleaseInput,
  PublishReleaseResponse,
  RefreshRequest,
  ReleaseDetailResponse,
  ReleaseListQuery,
  ReleaseListResponse,
  ReleaseResponse,
  ReleaseSummary,
  ReleaseTargetsQuery,
  RollbackReleaseInput,
  SessionUser,
  SetRolloutInput,
  SetupResponse,
  ShareBuildResponse,
  UpdateUserInput,
  UploadBuildInput,
  UserListResponse,
  UserResponse,
  UserSummary,
  VersionAdoptionResponse,
  VersionAdoptionRow,
} from "../contract.js";
import type { RootState } from "./index.js";
import { credentialsReceived, loggedOut } from "./auth.slice.js";

const rawBaseQuery = fetchBaseQuery({
  // Relative: the Vite dev proxy and the production deployment both serve the
  // API on the dashboard's own origin, so no base URL ever needs configuring.
  baseUrl: "/v1",
  prepareHeaders: (headers, { getState }) => {
    const { accessToken } = (getState() as RootState).auth;
    if (accessToken) headers.set("authorization", `Bearer ${accessToken}`);
    return headers;
  },
});

/**
 * Shared in-flight refresh. Access tokens are short-lived, so a page that
 * mounts six queries at once gets six simultaneous 401s; without this they
 * would each rotate the refresh token and five of the six rotations would be
 * rejected as replays, logging the operator out mid-session.
 */
let refreshInFlight: Promise<LoginResponse | null> | null = null;

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status !== 401) return result;

  const { refreshToken } = (api.getState() as RootState).auth;
  if (!refreshToken) {
    api.dispatch(loggedOut());
    return result;
  }

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshed = await rawBaseQuery(
        { url: "/auth/refresh", method: "POST", body: { refreshToken } satisfies RefreshRequest },
        api,
        extraOptions,
      );
      return refreshed.data ? (refreshed.data as LoginResponse) : null;
    })();
  }

  const session = await refreshInFlight;
  refreshInFlight = null;

  if (!session) {
    api.dispatch(loggedOut());
    return result;
  }
  api.dispatch(credentialsReceived(session));
  return rawBaseQuery(args, api, extraOptions);
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Release", "Build", "App", "User", "Audit", "Metrics", "Setup"],
  endpoints: (build) => ({
    /* ---------------------------------------------------------------- auth */
    login: build.mutation<LoginResponse, LoginRequest>({
      query: (body) => ({ url: "/auth/login", method: "POST", body }),
    }),
    refresh: build.mutation<LoginResponse, RefreshRequest>({
      query: (body) => ({ url: "/auth/refresh", method: "POST", body }),
    }),
    logout: build.mutation<LogoutResponse, RefreshRequest>({
      query: (body) => ({ url: "/auth/logout", method: "POST", body }),
    }),
    changePassword: build.mutation<LoginResponse, ChangePasswordRequest>({
      query: (body) => ({ url: "/auth/change-password", method: "POST", body }),
    }),
    me: build.query<SessionUser, void>({
      query: () => "/auth/me",
    }),

    /* --------------------------------------------------------------- users */
    listUsers: build.query<UserSummary[], void>({
      query: () => "/users",
      transformResponse: (response: UserListResponse) => response.users,
      providesTags: ["User"],
    }),
    createUser: build.mutation<UserSummary, CreateUserRequest>({
      query: (body) => ({ url: "/users", method: "POST", body }),
      transformResponse: (response: UserResponse) => response.user,
      invalidatesTags: ["User", "Audit"],
    }),
    updateUser: build.mutation<UserSummary, UpdateUserInput>({
      query: ({ id, ...body }) => ({ url: `/users/${id}`, method: "PATCH", body }),
      transformResponse: (response: UserResponse) => response.user,
      invalidatesTags: ["User", "Audit"],
    }),

    /* --------------------------------------------------------------- audit */
    listAudit: build.query<AuditListResponse, AuditQuery>({
      query: ({ action, actor, limit, offset }) => ({
        url: "/audit",
        params: { action: action || undefined, actor: actor || undefined, limit, offset },
      }),
      providesTags: ["Audit"],
    }),

    /* ---------------------------------------------------------------- apps */
    listApps: build.query<AppSummary[], void>({
      query: () => "/apps",
      transformResponse: (response: AppListResponse) => response.apps,
      providesTags: ["App"],
    }),
    createApp: build.mutation<AppSummary, CreateAppRequest>({
      query: (body) => ({ url: "/apps", method: "POST", body }),
      transformResponse: (response: AppResponse) => response.app,
      invalidatesTags: ["App", "Audit"],
    }),
    createChannel: build.mutation<AppSummary, CreateChannelRequest>({
      query: ({ slug, name }) => ({ url: `/apps/${slug}/channels`, method: "POST", body: { name } }),
      transformResponse: (response: AppResponse) => response.app,
      invalidatesTags: ["App", "Audit"],
    }),

    /* ------------------------------------------------------------ releases */
    listReleases: build.query<ReleaseListResponse, ReleaseListQuery>({
      query: ({ app, channel, platform, status, limit, offset }) => ({
        url: "/releases",
        params: { app, channel, platform, status, limit, offset },
      }),
      providesTags: ["Release"],
    }),
    getRelease: build.query<ReleaseDetailResponse, string>({
      query: (id) => `/releases/${id}`,
      providesTags: ["Release"],
    }),
    publishRelease: build.mutation<PublishReleaseResponse, PublishReleaseInput>({
      query: ({ bundle, meta }) => {
        const form = new FormData();
        // `meta` before the file: multer streams parts in order, so the JSON
        // part must already be parsed when the upload handler sees the bundle.
        form.append("meta", JSON.stringify(meta));
        form.append("bundle", bundle, bundle.name);
        return { url: "/releases", method: "POST", body: form };
      },
      // The compatibility report travels with the release, so the envelope is
      // kept whole: the review step needs both halves.
      invalidatesTags: ["Release", "Audit", "Metrics"],
    }),
    // `confirmNoDevices` is only sent after the operator has been shown that
    // the release would reach nobody; the server rejects the call otherwise.
    activateRelease: build.mutation<ReleaseSummary, ActivateReleaseInput>({
      query: ({ id, confirmNoDevices }) => ({
        url: `/releases/${id}/activate`,
        method: "POST",
        body: { confirmNoDevices: confirmNoDevices ?? false },
      }),
      transformResponse: (response: ReleaseResponse) => response.release,
      invalidatesTags: ["Release", "Audit", "Metrics"],
    }),
    releaseTargets: build.query<KnownTargetsResponse, ReleaseTargetsQuery>({
      query: ({ app, channel, platform }) => ({
        url: "/releases/targets",
        params: { app, channel, platform },
      }),
      providesTags: ["Release", "Build"],
    }),
    // Republishes an existing bundle against another native target; the bytes
    // are reused server-side, so nothing is uploaded again here.
    copyRelease: build.mutation<PublishReleaseResponse, CopyReleaseInput>({
      query: ({ id, channel, fingerprint, nativeVersion, activate, confirmNoDevices }) => ({
        url: `/releases/${id}/copy`,
        method: "POST",
        body: { channel, fingerprint, nativeVersion, activate, confirmNoDevices },
      }),
      invalidatesTags: ["Release", "Audit", "Metrics"],
    }),
    haltRelease: build.mutation<ReleaseSummary, HaltReleaseInput>({
      query: ({ id, reason }) => ({ url: `/releases/${id}/halt`, method: "POST", body: { reason } }),
      transformResponse: (response: ReleaseResponse) => response.release,
      invalidatesTags: ["Release", "Audit", "Metrics"],
    }),
    rollbackRelease: build.mutation<ReleaseSummary, RollbackReleaseInput>({
      query: ({ id, toSeq, reason }) => ({
        url: `/releases/${id}/rollback`,
        method: "POST",
        body: { toSeq, reason },
      }),
      transformResponse: (response: ReleaseResponse) => response.release,
      invalidatesTags: ["Release", "Audit", "Metrics"],
    }),
    setRollout: build.mutation<ReleaseSummary, SetRolloutInput>({
      query: ({ id, rolloutPercent }) => ({
        url: `/releases/${id}/rollout`,
        method: "PATCH",
        body: { rolloutPercent },
      }),
      transformResponse: (response: ReleaseResponse) => response.release,
      invalidatesTags: ["Release", "Audit"],
    }),
    deleteRelease: build.mutation<DeleteReleaseResponse, string>({
      // Same invalidations as the other release mutations: the list and the
      // detail drawer (Release), the audit trail the server writes, and the
      // metrics/setup panels that count releases per channel.
      query: (id) => ({ url: `/releases/${id}`, method: "DELETE" }),
      invalidatesTags: ["Release", "Audit", "Metrics"],
    }),

    /* -------------------------------------------------------------- builds */
    // Native binaries, not OTA bundles: no lifecycle, no rollout, no
    // fingerprint matching — a build is a file a tester installs by hand.
    listBuilds: build.query<BuildListResponse, BuildListQuery>({
      query: ({ app, channel, platform, limit, offset }) => ({
        url: "/builds",
        params: { app, channel, platform, limit, offset },
      }),
      providesTags: ["Build"],
    }),
    // Binaries do not go through RTK Query: `fetch` reports no upload
    // progress, so they are sent by store/upload-manager.ts over XHR, which
    // invalidates `Build` and `Audit` here when a transfer finishes.
    // Minting a share link writes an audit entry but changes no build row,
    // so only the audit trail is invalidated.
    shareBuild: build.mutation<ShareBuildResponse, string>({
      query: (id) => ({ url: `/builds/${id}/share`, method: "POST" }),
      invalidatesTags: ["Audit"],
    }),
    // Declaring a build the channel's native target changes what every older
    // install is told on its next heartbeat, so Setup (which shows the target)
    // and the audit trail both go stale.
    promoteBuild: build.mutation<PromoteBuildResponse, PromoteBuildInput>({
      query: ({ id, note, blocking }) => ({
        url: `/builds/${id}/promote`,
        method: "POST",
        body: { note, blocking },
      }),
      invalidatesTags: ["Build", "Audit", "Setup"],
    }),
    deleteBuild: build.mutation<DeleteBuildResponse, string>({
      query: (id) => ({ url: `/builds/${id}`, method: "DELETE" }),
      invalidatesTags: ["Build", "Audit"],
    }),

    /* ------------------------------------------------------------- metrics */
    activeUsers: build.query<ActiveUsersResponse, AnalyticsParams>({
      query: ({ app, channel, platform, from, to, actor }) => ({
        url: "/metrics/active-users",
        params: { app, channel, platform, from, to, actor },
      }),
      providesTags: ["Metrics"],
    }),
    features: build.query<FeatureUsageRow[], FeatureUsageQuery>({
      query: ({ app, channel, platform, from, to, actor, limit }) => ({
        url: "/metrics/features",
        params: { app, channel, platform, from, to, actor, limit },
      }),
      transformResponse: (response: FeatureUsageResponse) => response.rows,
      providesTags: ["Metrics"],
    }),
    // Fetched only once a row is clicked: the employee list behind one key is
    // far larger than the table it is opened from, and nothing on the page
    // needs it until then.
    featureDetail: build.query<FeatureDetailResponse, FeatureDetailParams>({
      query: ({ app, channel, platform, from, to, actor, type, key, q, limit }) => ({
        url: "/metrics/feature-detail",
        params: { app, channel, platform, from, to, actor, type, key, q, limit },
      }),
      providesTags: ["Metrics"],
    }),
    adoption: build.query<VersionAdoptionRow[], AnalyticsParams>({
      query: ({ app, channel, platform, from, to, actor }) => ({
        url: "/metrics/adoption",
        params: { app, channel, platform, from, to, actor },
      }),
      transformResponse: (response: VersionAdoptionResponse) => response.rows,
      providesTags: ["Metrics"],
    }),
    overview: build.query<OverviewResponse, OverviewParams>({
      query: ({ app, channel, actor }) => ({
        url: "/metrics/overview",
        params: { app, channel, actor },
      }),
      providesTags: ["Metrics", "Release"],
    }),

    /* ---------------------------------------------------------------- setup */
    // Also tagged `Release`: the page shows the active release seq per channel,
    // and `App` because a newly created app or channel belongs on it too.
    getSetup: build.query<SetupResponse, void>({
      query: () => "/setup",
      providesTags: ["Setup", "Release", "App"],
    }),
  }),
});

export const {
  useLoginMutation,
  useRefreshMutation,
  useLogoutMutation,
  useChangePasswordMutation,
  useMeQuery,
  useListUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useListAuditQuery,
  useListAppsQuery,
  useCreateAppMutation,
  useCreateChannelMutation,
  useListReleasesQuery,
  useGetReleaseQuery,
  usePublishReleaseMutation,
  useActivateReleaseMutation,
  useReleaseTargetsQuery,
  useCopyReleaseMutation,
  useHaltReleaseMutation,
  useRollbackReleaseMutation,
  useSetRolloutMutation,
  useDeleteReleaseMutation,
  useListBuildsQuery,
  useShareBuildMutation,
  usePromoteBuildMutation,
  useDeleteBuildMutation,
  useActiveUsersQuery,
  useFeaturesQuery,
  useAdoptionQuery,
  useOverviewQuery,
  useFeatureDetailQuery,
  useGetSetupQuery,
} = api;
